import latestRunJson from './fixtures/latestRun.json';
import type { AgenticRun, RunSummary } from './types';
import { runSchema } from './types';

const OLDER_RUN_ID = 'FIXTURE_RUN_OLDER_COMPLETED';
const FAILED_RUN_ID = 'FIXTURE_RUN_FAILED';
const RUNNING_RUN_ID = 'FIXTURE_RUN_RUNNING';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Built fresh on every call rather than once at import: a module-level `runningRun` with a fixed
 * timestamp goes stale the moment it is older than the code that reads it — this fixture's hardcoded
 * date was twelve days in the past, so sample mode's "running" row was silently rendering as Failed,
 * and `fixtures.spec.ts` stayed green only because it asserted on the raw `status` field rather than
 * the effective one a viewer actually sees.
 */
const buildRuns = () => {
  /**
   * The real output of run 01KZ6Y420FS55S0CXQ7EKT7KFX (loop 1, 2026-08-04), with the organization id
   * replaced. Parsed rather than cast: TypeScript widens the JSON's literals to string, so a cast
   * would be needed to satisfy the discriminated unions — and would also mask exactly the contract
   * drift this fixture exists to catch.
   */
  const sampleRun: AgenticRun = runSchema.parse(latestRunJson);

  /**
   * Their `findings`/`toolsUsed`/`usage` mirror the actual shape a `failed` or `running` row has in
   * production — empty, per `executeRun`'s stub — rather than inventing content, so a component built
   * against this fixture doesn't silently assume a shape that never occurs for real. No `heartbeatAt`
   * on the two terminal ones — leaving it off proves a reader never needs it on a settled run.
   */
  const olderCompletedRun: AgenticRun = {
    ...sampleRun,
    runId: OLDER_RUN_ID,
    generatedAt: new Date(Date.now() - 2 * DAY_MS).toISOString(),
    status: 'completed',
    findings: sampleRun.findings.slice(0, 3),
    grounding: undefined,
    reconciliation: undefined,
  };

  const failedRun: AgenticRun = {
    ...sampleRun,
    runId: FAILED_RUN_ID,
    generatedAt: new Date(Date.now() - 3 * DAY_MS).toISOString(),
    status: 'failed',
    failure: { code: 'contextLimit' },
    findings: [],
    toolsUsed: [],
    iterations: 2,
    usage: { inputTokens: 4200, outputTokens: 900 },
    grounding: undefined,
    reconciliation: undefined,
  };

  const runningRun: AgenticRun = {
    ...sampleRun,
    runId: RUNNING_RUN_ID,
    generatedAt: new Date().toISOString(),
    heartbeatAt: new Date().toISOString(),
    stage: 'grounding',
    attempt: 0,
    status: 'running',
    findings: [],
    toolsUsed: [],
    iterations: 0,
    usage: { inputTokens: 0, outputTokens: 0 },
    grounding: undefined,
    reconciliation: undefined,
  };

  return { sampleRun, olderCompletedRun, failedRun, runningRun };
};

export const getLatestRunFixture = (loopId: number): AgenticRun => ({
  ...buildRuns().sampleRun,
  loopId,
});

const toSummary = (run: AgenticRun): RunSummary => ({
  runId: run.runId,
  generatedAt: run.generatedAt,
  status: run.status,
  heartbeatAt: run.heartbeatAt,
  stage: run.stage,
  model: run.model,
  findingCount: run.findings.length,
});

/**
 * Newest first, matching the real list endpoint's contract. Sorted by `generatedAt` rather than
 * hardcoded in that order: `sampleRun` carries a real, fixed historical date, while the synthetic
 * rows are stamped relative to "now" — which one sorts where depends on how far the fixture's fixed
 * date has drifted from the current date, not on the order they're constructed in.
 */
export const getRunListFixture = (loopId: number): RunSummary[] => {
  const { runningRun, sampleRun, olderCompletedRun, failedRun } = buildRuns();
  return [runningRun, sampleRun, olderCompletedRun, failedRun]
    .map((run) => toSummary({ ...run, loopId }))
    .sort((a, b) => Date.parse(b.generatedAt) - Date.parse(a.generatedAt));
};

/**
 * Mirrors the real by-id endpoint's contract: `null` for a runId the mock data source doesn't know
 * about, same as a 404 would resolve on the real path.
 */
export const getRunFixture = (loopId: number, runId: string): AgenticRun | null => {
  const { sampleRun, olderCompletedRun, failedRun, runningRun } = buildRuns();
  const runsById = new Map(
    [sampleRun, olderCompletedRun, failedRun, runningRun].map((run) => [run.runId, run]),
  );
  const run = runsById.get(runId);
  return run ? { ...run, loopId } : null;
};
